<script lang="ts">
  import {
    Navbar,
    NavBrand,
    NavLi,
    NavUl,
    NavHamburger,
    Avatar,
    Dropdown,
    DropdownItem,
    DropdownHeader,
    DropdownDivider,
    Button,
    Tooltip,
  } from "flowbite-svelte";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import {
    faMoon,
    faSun,
    faChevronDown,
    faTimes,
  } from "@fortawesome/free-solid-svg-icons";
  import { Sun as IconSun, Moon as IconMoon } from "lucide-svelte";
  import { navigateTo } from "../../services/routing/route-action.js";
  import { theme } from "../../services/stores/theme-store.js";
  import { ROUTE } from "../../constants/route-constants.js";
  import { LABEL } from "../../constants/label-constants.js";
  import { userStore } from "../../services/stores/user-store.js";
  import type { User } from "../../services/api/types.js";
  import { UserRole } from "../../services/api/types.js";
  import { onMount } from "svelte";
  import { isAuthenticated } from "../../services/security/auth-handler.js";

  let user: User | null = null;
  let userType: string = "";
  let customerId: string = "";
  userStore.subscribe((value) => {
    if (!value.user) return;
    user = value.user;
    userType = value.user.user_type || "";
    if (
      userType === "customer" &&
      value.user.customers &&
      Array.isArray(value.user.customers) &&
      value.user.customers.length > 0
    ) {
      customerId = value.user.customers[0].customer_id || "";
    }
  });

  let nonActiveClass = "md:hover:bg-transparent border-none";
  let dropdownOpen = false;
  let avatarDropdownOpen = false;

  function loadContent(event: Event, routeName: string) {
    event.preventDefault();
    navigateTo(routeName);
  }

  let isDark = true;

  function themeToggle() {
    theme.update((currentTheme) =>
      currentTheme === "dark" ? "light" : "dark"
    );
  }

  $: isDark = $theme === "dark";
  $: icon = isDark ? faSun : faMoon;

  $: userInitials =
    user && user.name && user.name.length > 0
      ? user.name
          .split(" ")
          .map((n: string) => n[0])
          .join("")
          .toUpperCase()
      : "?";

  let version = "...";
  let expiration = "...";
  let termsContent = "";

  $: userRoleDisplay = 
    userType === "oh-user" && user?.user_role
      ? `OH ${user.user_role.charAt(0).toUpperCase() + user.user_role.slice(1)}`
      : "";

  function formatExpirationDate(date: Date): string {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Reset hours/minutes/seconds to compare just the dates
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const todayOnly = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const tomorrowOnly = new Date(
      tomorrow.getFullYear(),
      tomorrow.getMonth(),
      tomorrow.getDate()
    );

    // Format time part
    const hour = date.getHours() % 12 || 12;
    const minute = date.getMinutes().toString().padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";
    const timeStr = `${hour}:${minute} ${ampm}`;

    if (dateOnly.getTime() === todayOnly.getTime()) {
      return `Today ${timeStr}`;
    } else if (dateOnly.getTime() === tomorrowOnly.getTime()) {
      return `Tomorrow ${timeStr}`;
    } else {
      const day = date.getDate();
      const month = date.toLocaleString("en-US", { month: "long" });
      return `${day} ${month} ${timeStr}`;
    }
  }

  onMount(async () => {
    try {
      const response = await fetch("/version.txt");
      version = (await response.text()).trim();
    } catch (error) {
      version = "unknown";
    }

    // Get expiration time - this only changes after a new login
    const readableExp = localStorage.getItem("tokenExpirationReadable");
    if (readableExp) {
      const date = new Date(readableExp);
      expiration = formatExpirationDate(date);
    } else {
      expiration = "Not set";
      logInfo("No expiration set, signing out", "NavBar", undefined);
    }

    // Load terms and conditions
    try {
      const termsResponse = await fetch("/assets/help/terms.html");
      termsContent = await termsResponse.text();
    } catch (error) {
      termsContent = "Terms and conditions not available.";
    }
  });

  // Update expiration whenever auth state changes
  $: if ($isAuthenticated) {
    expiration = "Not set"; // Assuming expiration is not set after signout
  }
</script>

<Navbar class="navbar-component">
  <NavBrand href="/">
    <svg
      class="ml-2"
      role="graphics-document"
      xmlns="http://www.w3.org/2000/svg"
      width="179"
      height="36"
      viewBox="0 0 239 48"
      fill="none"
      preserveAspectRatio="xMinYMid meet"
    >
      <path
        d="M8.69434 24C8.69434 15.5774 15.5774 8.69434 24 8.69434C32.4226 8.69434 39.3057 15.5774 39.3057 24C39.3057 32.4226 32.4226 39.3057 24 39.3057C15.5774 39.3057 8.69434 32.4226 8.69434 24ZM24 0C10.7774 0 0 10.7774 0 24C0 37.2226 10.7774 48 24 48C37.2226 48 48 37.2226 48 24C48 10.7774 37.2226 0 24 0Z"
        fill="url(#paint0_linear)"
      ></path>
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M186.294 15.2151L193.992 32.1509H191.638L189.736 27.9849H182.853L180.951 32.1509H178.596L186.294 15.2151ZM186.204 20.2868L183.577 26.083H188.83L186.204 20.2868ZM61.0415 18.0227C59.5019 19.6529 58.6868 21.5548 58.6868 24C58.6868 26.4453 59.4113 28.4378 60.951 29.9774C62.4906 31.517 64.483 32.3321 66.8377 32.3321C69.1925 32.3321 71.1849 31.517 72.7245 29.9774C74.2642 28.3472 75.0793 26.3548 75.0793 24C75.0793 21.5548 74.2642 19.5623 72.7245 18.0227C71.1849 16.4831 69.1925 15.668 66.7472 15.668C64.483 15.668 62.6717 16.4831 61.0415 18.0227ZM63.5774 20.3774C64.3925 19.3812 65.4792 18.9283 66.8377 18.9283C68.2868 18.9283 69.3736 19.4717 70.0981 20.3774C70.9132 21.2831 71.366 22.4604 71.366 24C71.366 25.5397 70.9132 26.8076 70.0981 27.7133C69.283 28.7095 68.1962 29.1623 66.8377 29.1623C65.4792 29.1623 64.3925 28.6189 63.5774 27.7133C62.7623 26.717 62.3094 25.5397 62.3094 24.0906C62.3094 22.551 62.7623 21.3736 63.5774 20.3774ZM87.2151 15.8491H81.6906V32.151H85.1321V25.8114L87.1245 25.7208C87.7585 25.7208 88.5736 25.5397 89.4793 25.0869C90.4755 24.634 91.1094 24.0001 91.5623 23.185C92.0151 22.3699 92.1962 21.4642 92.1962 20.5586C92.1962 19.1095 91.7434 18.0227 90.8377 17.117C89.9321 16.302 88.7547 15.8491 87.2151 15.8491ZM85.1321 22.9133V18.7472L85.9472 18.6567C86.7623 18.6567 87.4868 18.8378 87.9396 19.2001C88.3925 19.5623 88.6642 20.1057 88.6642 20.7397C88.6642 21.3736 88.483 21.8265 88.0302 22.2793C87.5774 22.7321 87.034 22.9133 86.4906 22.9133H85.1321ZM98.3547 15.8491H107.502V19.1095H101.887V22.3699H107.502V25.5397H101.887V28.8906H107.502V32.151H98.3547V15.8491ZM128.785 15.8491H125.343H125.253V26.3548L117.826 15.8491H114.385V32.151H117.917V21.6453L125.343 32.151H128.785V15.8491ZM144.815 15.8491H147.079V22.0982H154.868V15.8491H157.042V32.151H154.868V24.3623H147.079V32.151H144.815V15.8491ZM173.434 15.8491H164.377V32.151H173.434V29.9774H166.642V24.3623H173.434V22.1887H166.642V18.1133H173.434V15.8491ZM201.962 15.8491H199.789V32.151H207.306V29.9774H201.962V15.8491ZM210.566 15.8491H220.528V18.1133H216.634V32.151H214.46V18.1133H210.566V15.8491ZM228.679 15.8491H226.415V32.151H228.679V24.3623H236.468V32.151H238.642V15.8491H236.468V22.0982H228.679V15.8491Z"
        fill="#ffffff"
      ></path>
      <defs>
        <linearGradient
          id="paint0_linear"
          x1="0.00975798"
          y1="47.9948"
          x2="48"
          y2="47.9948"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#808080"></stop>
          <stop offset="0.5" stop-color="#A0A0A0"></stop>
          <stop offset="0.75" stop-color="#D0D0D0"></stop>
          <stop offset="1" stop-color="#FFFFFF"></stop>
        </linearGradient>
      </defs>
    </svg>
    <div class="h-[2px] bg-white"></div>
    <h1 style="font-size: 1.25rem; color: white;">
      <span class="app-logo-p1 ml-4">i</span><span class="app-logo-p2">SLR</span
      >
    </h1>
  </NavBrand>
  <NavUl
    ulClass="!bg-transparent flex flex-row items-center space-x-4"
    divClass="md:block md:w-auto shocking !w-auto"
    class="navbar-commands border-none"
    hidden={false}
  >
    <NavLi
      href="#"
      data-testid="nav-home"
      on:click={(event) => loadContent(event, ROUTE.HOME)}>{LABEL.HOME}</NavLi
    >

    {#if userType === "oh-user"}
      <NavLi href="#" data-testid="nav-customers" on:click={(event) => loadContent(event, ROUTE.CUSTOMERS)}>{LABEL.CUSTOMERS}</NavLi>
      <NavLi class="cursor-pointer" data-testid="nav-configure">Configure <FontAwesomeIcon icon={faChevronDown} class="w-3 h-3 inline" /></NavLi>
      <Dropdown bind:open={dropdownOpen} class="navbar-subcommands w-44 z-16">
        <DropdownItem href="#" data-testid="nav-users" on:click={(event) => { loadContent(event, ROUTE.USERS); dropdownOpen = false; }}>{LABEL.USERS}</DropdownItem>
        <DropdownItem href="#" data-testid="nav-lookups" on:click={(event) => { loadContent(event, ROUTE.LOOKUPS); dropdownOpen = false; }}>{LABEL.LOOKUPS}</DropdownItem>
      </Dropdown>
    {:else if userType === "customer"}
      {#if customerId}
        <NavLi href="#" data-testid="nav-results" on:click={(event) => loadContent(event, ROUTE.RESULTS + "?id=" + customerId)}>{LABEL.RESULTS}</NavLi>
      {:else}
        <NavLi href="#" data-testid="nav-results-not-configured">{LABEL.RESULTS} NOT CONFIGURED</NavLi>
      {/if}
    {/if}
  </NavUl>
  <div class="grow" />
  <div class="flex items-center gap-2 mr-2">
    <Button pill={true}
      outline={true}
      class="navbar-button"
      size="md"
      id="theme-toggle-btn"
      on:click={themeToggle}
    >
      {#key icon}
        <!-- <FontAwesomeIcon {icon} class="theme-mode {isDark ? 'sun-icon' : 'moon-icon'}" /> -->
        {#if isDark}
            <IconSun class="theme-mode sun-icon" size="24" />
        {:else}
          <IconMoon class="theme-mode moon-icon" size="24" />
        {/if}
      {/key}
    </Button>
    <Tooltip triggeredBy="#theme-toggle-btn" placement="bottom">
      Change to {isDark ? 'light' : 'dark'} mode
    </Tooltip>
  </div>
  <div class="flex items-center md:order-2">
    <Avatar id="avatar" border size="sm" class="cursor-pointer"
      >{userInitials}</Avatar
    >
  </div>
  <Dropdown class="avatar-menu" placement="bottom-end" triggeredBy="#avatar" bind:open={avatarDropdownOpen}>
    <DropdownHeader class="relative">
      <Button class="absolute top-0 right-2 p-1 close-button" size="xs" color="light" on:click={() => avatarDropdownOpen = false}>
        <FontAwesomeIcon icon={faTimes} />
      </Button>
      <div class="flex justify-between items-start h-16">
        <div class="flex-1">
          <span class="block app-info-user">{user ? user.name : "Unknown"}</span>
          {#if userRoleDisplay}
            <span class="block app-info-label user-role-text">{userRoleDisplay}</span>
          {/if}
          <span class="block app-info-label">Session Expires: {expiration}</span
          >
        </div>
        <div class="sign-out-button-container">
          <Button
            class="sign-out-button-header"
            size="sm"
            on:click={userStore.signOut}
          >
            Sign out
          </Button>
        </div>
      </div>
    </DropdownHeader>
    <DropdownDivider />
    <div class="px-4 py-2 terms-content">
      {@html termsContent}
    </div>
    <DropdownDivider />
    <div class="px-4 py-2 app-info-footer flex items-center justify-between">
      <div class="flex items-center">
        <img src="/assets/images/logo_grey.svg" alt="Open Health" class="w-5 h-5 mr-1" />
        <span class="app-info-label">iSLR version {version}</span>
      </div>
      <div class="flex items-center">
        <span class="app-info-label">Powered by:</span>
        <a href="https://www.openhealthgroup.com/" target="_blank" rel="noopener noreferrer" class="app-info-text app-link ml-1">OPEN Health</a>
      </div>
    </div>
  </Dropdown>
</Navbar>
